import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CreditCard,
  Loader2,
} from "lucide-react";
import { useRef, useState } from "react";
import type { RateCardRate } from "@/api/billing";
import { SkeletonRow } from "@/components/common/skeleton-row";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRateCard } from "../hooks/useRateCard";

function formatRate(rate: RateCardRate): string {
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: rate.currency,
  });
  const currencyFractionDigits =
    currencyFormatter.resolvedOptions().maximumFractionDigits;
  if (currencyFractionDigits === undefined) {
    throw new RangeError(`Unable to resolve minor units for ${rate.currency}`);
  }
  const decimalFractionDigits =
    rate.unit_amount_decimal.split(".")[1]?.length ?? 0;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: rate.currency,
    minimumFractionDigits: currencyFractionDigits,
    maximumFractionDigits: currencyFractionDigits + decimalFractionDigits,
  });
  const amount =
    Number(rate.unit_amount_decimal) / 10 ** currencyFractionDigits;
  const unit =
    rate.key === "clinical_note_generation_request" ? "note" : rate.unit;
  return `${formatter.format(amount)} per ${unit}`;
}

/**
 * NUX pricing step: the live self-serve rate card, plus the card-on-file
 * requirement the payment step enforces next. Mounting initiates the cached
 * rate-card query; failures remain inline and can be explicitly refetched.
 *
 * @param onContinue - Prepares payment and advances after it succeeds.
 * @param onBack - Returns to organization editing.
 * @returns The pricing, recovery, payment-preparation, and progression UI.
 */
export const PricingStep = ({
  onContinue,
  onBack,
}: {
  onContinue: () => Promise<void>;
  onBack: () => void;
}) => {
  const { data: rateCard, isLoading, isError, refetch } = useRateCard();
  const [isRetrying, setIsRetrying] = useState(false);
  const hasPricing = Boolean(rateCard?.rates.length);
  const continueInFlight = useRef(false);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [prepareError, setPrepareError] = useState(false);

  const handleContinue = async () => {
    if (continueInFlight.current) return;

    continueInFlight.current = true;
    setIsPreparingPayment(true);
    setPrepareError(false);
    try {
      await onContinue();
    } catch {
      setPrepareError(true);
    } finally {
      continueInFlight.current = false;
      setIsPreparingPayment(false);
    }
  };

  const retryRateCard = async () => {
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <>
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Pricing</h2>
        <p className="mt-3 text-muted-foreground">
          {rateCard?.description ??
            "Usage-based pricing, straight from our live rate card."}
        </p>
      </div>

      {isLoading && !isRetrying && <SkeletonRow count={4} />}

      {(isError || isRetrying) && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>We couldn't load pricing. Please try again.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void retryRateCard()}
              disabled={isRetrying}
              aria-busy={isRetrying}
            >
              {isRetrying ? "Retrying..." : "Retry"}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {rateCard && (
        <Card>
          <CardContent className="p-0">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-2/3">Product</TableHead>
                  <TableHead className="w-1/3">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rateCard.rates.map((rate) => (
                  <TableRow key={rate.key}>
                    <TableCell className="font-medium whitespace-normal break-words">
                      {rate.product_name}
                    </TableCell>
                    <TableCell className="whitespace-normal break-words">
                      {formatRate(rate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="flex items-start space-x-3 p-4 rounded-lg border">
        <CreditCard className="mt-0.5 w-5 h-5 text-primary" />
        <p className="text-sm text-muted-foreground">
          To sign up, save the billing card in your Stripe wallet. You'll add it
          in the next step; usage is billed to it monthly.
        </p>
      </div>

      {prepareError && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            We couldn't start the payment setup. Please try again.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          variant="outline"
          className="h-12 w-full sm:w-auto"
          onClick={onBack}
          disabled={isPreparingPayment}
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleContinue}
          className="group h-12 w-full text-base sm:flex-1"
          size="lg"
          disabled={
            isLoading ||
            isError ||
            isRetrying ||
            !hasPricing ||
            isPreparingPayment
          }
          aria-busy={isPreparingPayment}
        >
          {isPreparingPayment ? (
            <>
              <Loader2
                className="mr-2 w-4 h-4 animate-spin"
                aria-hidden="true"
              />
              Preparing Payment...
            </>
          ) : prepareError ? (
            <>
              Retry Payment Setup
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </>
          ) : (
            <>
              Continue to Payment
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </Button>
      </div>
    </>
  );
};
